import { useState, useEffect, useMemo } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  useSensor,
  useSensors,
  useDraggable,
  useDroppable,
} from '@dnd-kit/core';
import type { DragEndEvent, DragStartEvent } from '@dnd-kit/core';
import { useProjects, useTransitionProject, useLabels, useUpdateProject } from '../../hooks/useProjects';
import { useAuth } from '../../services/authContext';
import { StatusBadge } from '../../components/workflow/StatusBadge';
import { Button } from '../../components/common/Button';
import { Spinner } from '../../components/common/Spinner';
import { ConfirmModal } from '../../components/common/Modal';
import { DismissibleErrorBanner } from '../../components/common/DismissibleErrorBanner';
import { transitionProject } from '../../services/api/projects';
import type { MergeProjectsResult } from '../../services/api/projects';
import ProjectEditModal from '../../components/domain/ProjectEditModal';
import { ProjectMergeModal } from '../../components/domain/ProjectMergeModal';
// Phase 41 Plan 41-10 (TYPE-03 surfacing) — TypePill column.
import { TypePill } from '../../components/ui/TypePill';
import { projectTypeTone } from './projectTypeTone';
import { labelChipStyle } from '../../utils/labelColor';


const PROJECT_STATES = ['all', 'draft', 'in_progress', 'paused', 'completed', 'cancelled', 'archived'];

/**
 * Drag-to-reparent row: the whole <tr> is both the drag source and a drop
 * target. Clicking links/buttons inside still works — the PointerSensor's
 * 8px activation distance separates click from drag. Dropping row A onto
 * row B sets A.parentId = B.id; the floating row ghost is rendered by the
 * page-level <DragOverlay>.
 */
function ReparentRow({
  projectId,
  disabled,
  className,
  children,
}: {
  projectId: number;
  disabled: boolean;
  className: string;
  children: React.ReactNode;
}) {
  const { setNodeRef: setDropRef, isOver } = useDroppable({ id: projectId, disabled });
  const { setNodeRef: setDragRef, listeners, isDragging } = useDraggable({ id: projectId, disabled });

  return (
    <tr
      ref={(el) => {
        setDropRef(el);
        setDragRef(el);
      }}
      {...(disabled ? {} : listeners)}
      className={`${className} ${disabled ? '' : 'cursor-grab active:cursor-grabbing'} ${
        isOver ? 'bg-primary-100 ring-2 ring-inset ring-primary-400' : ''
      } ${isDragging ? 'opacity-30' : ''}`}
    >
      {children}
    </tr>
  );
}

/**
 * Drop zone for un-parenting: appears above the table only while a nested
 * project is being dragged; dropping there clears its parentId.
 */
function MakeTopLevelDropZone() {
  const { setNodeRef, isOver } = useDroppable({ id: 'make-top-level' });
  return (
    <div
      ref={setNodeRef}
      className={`mx-4 my-3 rounded-lg border-2 border-dashed px-4 py-3 text-center text-sm font-medium transition-colors ${
        isOver
          ? 'border-primary-500 bg-primary-50 text-primary-700'
          : 'border-gray-300 bg-gray-50 text-gray-500'
      }`}
    >
      Drop here to make it a top-level project
    </div>
  );
}

export default function ProjectListPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { isManager, isAdmin } = useAuth();
  const canManage = isManager || isAdmin;
  const [searchParams, setSearchParams] = useSearchParams();

  // Read initial values from URL params
  const [stateFilter, setStateFilter] = useState(searchParams.get('state') || 'in_progress');
  const [searchQuery, setSearchQuery] = useState(searchParams.get('search') || '');
  const [debouncedSearch, setDebouncedSearch] = useState(searchParams.get('search') || '');
  const [onlyMine, setOnlyMine] = useState(searchParams.get('onlyMine') === 'true');
  const [onlyParents, setOnlyParents] = useState(searchParams.get('onlyParents') === 'true');
  const [selectedLabelIds, setSelectedLabelIds] = useState<number[]>(() => {
    const labels = searchParams.get('labels');
    return labels ? labels.split(',').map(Number).filter(n => !isNaN(n)) : [];
  });
  const [managerFilter, setManagerFilter] = useState(searchParams.get('manager') || '');
  const [editingProjectId, setEditingProjectId] = useState<number | null>(null);
  const [archiveId, setArchiveId] = useState<number | null>(null);
  const [pauseId, setPauseId] = useState<number | null>(null);
  // F2-005: surface archive/pause mutation rejections inline instead of leaking
  // as unhandled promise rejections (which left the ConfirmModal stuck open).
  const [actionError, setActionError] = useState<string | null>(null);
  const [selectedProjectIds, setSelectedProjectIds] = useState<Set<number>>(new Set());
  const [showBulkArchiveModal, setShowBulkArchiveModal] = useState(false);
  const [isBulkArchiving, setIsBulkArchiving] = useState(false);
  // Quick 260805-gmk — admin-only merge of exactly 2 non-archived selected projects.
  const [showMergeModal, setShowMergeModal] = useState(false);
  const [sort, setSort] = useState<{ key: 'project' | 'manager'; dir: 'asc' | 'desc' }>({ key: 'project', dir: 'asc' });

  const toggleSort = (key: 'project' | 'manager') => {
    setSort((prev) => prev.key === key ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'asc' });
  };

  const { data: labels } = useLabels();

  // Update URL when filters change
  useEffect(() => {
    const params = new URLSearchParams();

    if (stateFilter && stateFilter !== 'in_progress') {
      params.set('state', stateFilter);
    }
    if (debouncedSearch) {
      params.set('search', debouncedSearch);
    }
    if (onlyMine) {
      params.set('onlyMine', 'true');
    }
    if (onlyParents) {
      params.set('onlyParents', 'true');
    }
    if (selectedLabelIds.length > 0) {
      params.set('labels', selectedLabelIds.join(','));
    }
    if (managerFilter) {
      params.set('manager', managerFilter);
    }

    setSearchParams(params, { replace: true });
  }, [stateFilter, debouncedSearch, onlyMine, onlyParents, selectedLabelIds, managerFilter, setSearchParams]);

  // Debounce search query to avoid refetching on every keystroke
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const { data: projects, isLoading, error } = useProjects({
    state: stateFilter !== 'all' ? stateFilter : undefined,
    search: debouncedSearch || undefined,
    onlyMine: onlyMine || undefined,
    onlyParents: onlyParents || undefined,
    labelIds: selectedLabelIds.length > 0 ? selectedLabelIds : undefined,
  });

  // Derive unique managers from loaded projects
  const managers = useMemo(() => {
    if (!projects) return [];
    const map = new Map<number, string>();
    for (const p of projects) {
      if (p.projectManager) {
        map.set(p.projectManager.id, p.projectManager.name);
      }
    }
    return [...map.entries()]
      .map(([id, name]) => ({ id, name }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [projects]);

  // Apply client-side manager filter
  const filteredProjects = useMemo(() => {
    if (!projects || !managerFilter) return projects;
    const managerId = parseInt(managerFilter, 10);
    return projects.filter((p) => p.projectManager?.id === managerId);
  }, [projects, managerFilter]);

  // Tree ordering (user request 2026-08-05): render parents first with their
  // children indented beneath them, depth-first, so the list mirrors the real
  // hierarchy. A child whose parent is filtered out of the current view is
  // promoted to a root (depth 0) — its ancestor path stays visible via the
  // fullName sub-line. Sorting applies within each sibling level.
  const treeRows = useMemo(() => {
    if (!filteredProjects) return [];
    const dir = sort.dir === 'asc' ? 1 : -1;
    const cmp = (a: (typeof filteredProjects)[number], b: (typeof filteredProjects)[number]) =>
      sort.key === 'project'
        ? (a.name ?? '').localeCompare(b.name ?? '') * dir
        : (a.projectManager?.name ?? '').localeCompare(b.projectManager?.name ?? '') * dir;

    const byId = new Map(filteredProjects.map((p) => [p.id, p]));
    const childrenOf = new Map<number, typeof filteredProjects>();
    const roots: typeof filteredProjects = [];
    for (const p of filteredProjects) {
      if (p.parent && byId.has(p.parent.id)) {
        const siblings = childrenOf.get(p.parent.id) ?? [];
        siblings.push(p);
        childrenOf.set(p.parent.id, siblings);
      } else {
        roots.push(p);
      }
    }

    const rows: { project: (typeof filteredProjects)[number]; depth: number }[] = [];
    const visit = (nodes: typeof filteredProjects, depth: number) => {
      for (const p of [...nodes].sort(cmp)) {
        rows.push({ project: p, depth });
        const kids = childrenOf.get(p.id);
        if (kids && depth < 10) visit(kids, depth + 1);
      }
    };
    visit(roots, 0);
    return rows;
  }, [filteredProjects, sort]);

  // Drag-to-reparent (user request 2026-08-05): drop project A onto project B
  // → A becomes B's child. Backend updateProject re-validates (self-parent +
  // cycle guards) and recascades fullName; the client guard below only covers
  // ancestors visible in the current filter for a friendlier message.
  const updateProjectMutation = useUpdateProject();
  const dndSensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 8 } }));
  const [activeDragId, setActiveDragId] = useState<number | null>(null);
  const draggedProject =
    activeDragId !== null ? filteredProjects?.find((p) => p.id === activeDragId) : undefined;

  const handleReparentDragStart = (event: DragStartEvent) => {
    setActiveDragId(Number(event.active.id));
  };

  const handleReparentDragEnd = (event: DragEndEvent) => {
    setActiveDragId(null);
    const activeId = Number(event.active.id);
    if (!filteredProjects) return;

    // Un-parent: dropped on the "make top-level" zone.
    if (event.over?.id === 'make-top-level') {
      const dragged = filteredProjects.find((p) => p.id === activeId);
      if (!dragged?.parent) return; // already top-level
      updateProjectMutation.mutate(
        { id: activeId, input: { parentId: null } },
        {
          onError: (err) =>
            setActionError(err instanceof Error ? err.message : 'Failed to move project'),
        }
      );
      return;
    }

    const overId = event.over ? Number(event.over.id) : NaN;
    if (!Number.isFinite(overId) || activeId === overId) return;
    const byId = new Map(filteredProjects.map((p) => [p.id, p]));
    const dragged = byId.get(activeId);
    const target = byId.get(overId);
    if (!dragged || !target) return;
    if (dragged.parent?.id === overId) return; // already a direct child

    let ancestor: typeof target | undefined = target;
    while (ancestor?.parent) {
      if (ancestor.parent.id === activeId) {
        setActionError('Cannot move a project under its own descendant.');
        return;
      }
      ancestor = byId.get(ancestor.parent.id);
    }

    updateProjectMutation.mutate(
      { id: activeId, input: { parentId: overId } },
      {
        onError: (err) =>
          setActionError(err instanceof Error ? err.message : 'Failed to move project'),
      }
    );
  };

  const toggleLabel = (labelId: number) => {
    setSelectedLabelIds((prev) =>
      prev.includes(labelId) ? prev.filter((id) => id !== labelId) : [...prev, labelId]
    );
  };

  const toggleProjectSelect = (projectId: number) => {
    setSelectedProjectIds((prev) => {
      const next = new Set(prev);
      if (next.has(projectId)) {
        next.delete(projectId);
      } else {
        next.add(projectId);
      }
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (!filteredProjects) return;
    const archivableProjects = filteredProjects.filter((p) => p.state !== 'archived');
    if (selectedProjectIds.size === archivableProjects.length) {
      setSelectedProjectIds(new Set());
    } else {
      setSelectedProjectIds(new Set(archivableProjects.map((p) => p.id)));
    }
  };

  const archiveMutation = useTransitionProject();

  const handleArchive = async () => {
    if (archiveId === null) return;
    setActionError(null);
    try {
      await archiveMutation.mutateAsync({ id: archiveId, action: 'archive' });
    } catch (err) {
      setActionError((err as Error).message || 'Failed to archive project');
    } finally {
      setArchiveId(null);
    }
  };

  const pauseMutation = useTransitionProject();

  const handlePause = async () => {
    if (pauseId === null) return;
    setActionError(null);
    try {
      await pauseMutation.mutateAsync({ id: pauseId, action: 'pause' });
    } catch (err) {
      setActionError((err as Error).message || 'Failed to pause project');
    } finally {
      setPauseId(null);
    }
  };

  const handleBulkArchive = async () => {
    const idsToArchive = Array.from(selectedProjectIds);
    setIsBulkArchiving(true);
    setActionError(null);
    try {
      const results = await Promise.allSettled(idsToArchive.map((id) => transitionProject(id, 'archive')));
      const failedIds: number[] = [];
      results.forEach((result, idx) => {
        if (result.status === 'rejected' && idsToArchive[idx] != null) failedIds.push(idsToArchive[idx]);
      });
      if (failedIds.length > 0) {
        setSelectedProjectIds(new Set(failedIds));
        // F8-006: replace blocking alert() with the existing DismissibleErrorBanner
        // pattern already rendered for actionError (line 215).
        setActionError(
          `Failed to archive ${failedIds.length} of ${idsToArchive.length} projects. The failed projects remain selected.`,
        );
      } else {
        setSelectedProjectIds(new Set());
      }
      setShowBulkArchiveModal(false);
      queryClient.invalidateQueries({ queryKey: ['projects'] });
    } finally {
      setIsBulkArchiving(false);
    }
  };

  // Quick 260805-gmk — the "Merge…" button only appears when exactly 2
  // non-archived projects are selected (both resolved from the current
  // filtered view so the modal always has fresh names).
  const mergeCandidates = useMemo(() => {
    if (!filteredProjects || selectedProjectIds.size !== 2) return null;
    const selected = filteredProjects.filter((p) => selectedProjectIds.has(p.id));
    if (selected.length !== 2) return null;
    if (selected.some((p) => p.state === 'archived')) return null;
    const [projectA, projectB] = selected;
    if (!projectA || !projectB) return null;
    return { projectA, projectB };
  }, [filteredProjects, selectedProjectIds]);

  const handleMerged = (result: MergeProjectsResult) => {
    setSelectedProjectIds(new Set());
    setShowMergeModal(false);
    queryClient.invalidateQueries({ queryKey: ['projects'] });
    if (!result.archived) {
      setActionError(
        `Merged ${result.movedTaskCount} task${result.movedTaskCount === 1 ? '' : 's'}, but the source project could not be archived automatically. Archive it manually if needed.`,
      );
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold text-gray-900">Projects</h1>
        <div className="flex items-center gap-2">
          {isAdmin && mergeCandidates && (
            <Button variant="secondary" onClick={() => setShowMergeModal(true)}>
              Merge…
            </Button>
          )}
          {isAdmin && selectedProjectIds.size > 0 && (
            <Button variant="danger" onClick={() => setShowBulkArchiveModal(true)}>
              Archive Selected ({selectedProjectIds.size})
            </Button>
          )}
          {canManage && (
            <Button onClick={() => navigate('/projects/new')}>
              New Project
            </Button>
          )}
        </div>
      </div>

      <DismissibleErrorBanner message={actionError} onDismiss={() => setActionError(null)} />

      {/* Filters */}
      <div className="space-y-3">
        <div className="flex flex-col sm:flex-row gap-4">
          <select
            value={stateFilter}
            onChange={(e) => setStateFilter(e.target.value)}
            aria-label="Filter projects by state"
            className="rounded-lg border border-gray-300 px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
          >
            {PROJECT_STATES.map((state) => (
              <option key={state} value={state}>
                {state === 'all' ? 'All States' : state.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
              </option>
            ))}
          </select>
          <select
            value={managerFilter}
            onChange={(e) => setManagerFilter(e.target.value)}
            aria-label="Filter projects by manager"
            className="rounded-lg border border-gray-300 px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
          >
            <option value="">All Managers</option>
            {managers.map((m) => (
              <option key={m.id} value={m.id}>
                {m.name}
              </option>
            ))}
          </select>
          <input
            type="text"
            placeholder="Search projects..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="flex-1 rounded-lg border border-gray-300 px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          />
          <label className="flex items-center gap-2 text-sm text-gray-700 whitespace-nowrap">
            <input
              type="checkbox"
              checked={onlyMine}
              onChange={(e) => setOnlyMine(e.target.checked)}
              className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
            />
            Only my projects
          </label>
          <label className="flex items-center gap-2 text-sm text-gray-700 whitespace-nowrap">
            <input
              type="checkbox"
              checked={onlyParents}
              onChange={(e) => setOnlyParents(e.target.checked)}
              className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
            />
            Only parents
          </label>
        </div>

        {/* Label Filters */}
        {labels && labels.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {labels.map((label) => (
              <label
                key={label.id}
                className="flex items-center gap-1.5 cursor-pointer"
              >
                <input
                  type="checkbox"
                  checked={selectedLabelIds.includes(label.id)}
                  onChange={() => toggleLabel(label.id)}
                  className="h-3.5 w-3.5 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                />
                <span
                  className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium"
                  style={{
                    ...labelChipStyle(label.color),
                  }}
                >
                  {label.name}
                </span>
              </label>
            ))}
          </div>
        )}
      </div>

      {/* Project List */}
      {isLoading ? (
        <div className="flex items-center justify-center h-64 bg-white rounded-lg border border-gray-200">
          <Spinner />
        </div>
      ) : error ? (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800">Failed to load projects: {(error as Error).message}</p>
        </div>
      ) : !filteredProjects || filteredProjects.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-lg border border-gray-200">
          <p className="text-gray-500">No projects found.</p>
          {!projects?.length && canManage && (
            <Button variant="ghost" onClick={() => navigate('/projects/new')} className="mt-4">
              Create your first project
            </Button>
          )}
        </div>
      ) : (
        <div className="bg-white shadow-sm rounded-lg border border-gray-200 overflow-hidden">
          <DndContext
            sensors={dndSensors}
            onDragStart={handleReparentDragStart}
            onDragEnd={handleReparentDragEnd}
            onDragCancel={() => setActiveDragId(null)}
          >
          {draggedProject?.parent && <MakeTopLevelDropZone />}
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                {isAdmin && (
                  <th className="px-4 py-3 w-10">
                    <input
                      type="checkbox"
                      checked={filteredProjects ? selectedProjectIds.size === filteredProjects.filter((p) => p.state !== 'archived').length && filteredProjects.filter((p) => p.state !== 'archived').length > 0 : false}
                      onChange={toggleSelectAll}
                      aria-label="Select all projects"
                      className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                    />
                  </th>
                )}
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  <button className="flex items-center gap-1 hover:text-gray-700" onClick={() => toggleSort('project')}>
                    Project {sort.key === 'project' ? (sort.dir === 'asc' ? '↑' : '↓') : ''}
                  </button>
                </th>
                {/* Phase 41 Plan 41-10 (TYPE-03 surfacing) — TypePill column */}
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Type
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  <button className="flex items-center gap-1 hover:text-gray-700" onClick={() => toggleSort('manager')}>
                    Manager {sort.key === 'manager' ? (sort.dir === 'asc' ? '↑' : '↓') : ''}
                  </button>
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  State
                </th>
                {canManage && (
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                )}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {treeRows.map(({ project, depth }) => (
                <ReparentRow
                  key={project.id}
                  projectId={project.id}
                  disabled={!canManage}
                  className={`hover:bg-gray-50 ${selectedProjectIds.has(project.id) ? 'bg-primary-50' : ''}`}
                >
                  {isAdmin && (
                    <td className="px-4 py-4">
                      {project.state !== 'archived' ? (
                        <input
                          type="checkbox"
                          checked={selectedProjectIds.has(project.id)}
                          onChange={() => toggleProjectSelect(project.id)}
                          aria-label={`Select project ${project.name}`}
                          className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                        />
                      ) : null}
                    </td>
                  )}
                  <td className="px-6 py-4">
                    <div style={depth > 0 ? { paddingLeft: `${depth * 1.5}rem` } : undefined}>
                      <Link to={`/projects/${project.id}`} className="text-primary-600 hover:text-primary-800 font-medium">
                        {depth > 0 && <span className="mr-1.5 text-gray-300 select-none">└</span>}
                        {project.name}
                      </Link>
                      {/* Ancestor path only for promoted orphans (parent not in
                          this view) — children under a visible parent show the
                          hierarchy structurally. */}
                      {depth === 0 && project.fullName && project.fullName !== project.name && (
                        <p className="text-sm text-gray-500 mt-0.5">{project.fullName}</p>
                      )}
                    </div>
                  </td>
                  {/* Phase 41 Plan 41-10 (TYPE-03 surfacing) — TypePill column */}
                  <td className="px-6 py-4">
                    {(() => {
                      const t = projectTypeTone(project.projectTypeId);
                      return <TypePill tone={t.tone} label={t.label} />;
                    })()}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-600">
                    {project.projectManager?.name ?? '-'}
                  </td>
                  <td className="px-6 py-4">
                    <StatusBadge state={project.state} size="sm" />
                  </td>
                  {canManage && (
                    <td className="px-6 py-4 text-right space-x-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setEditingProjectId(project.id)}
                      >
                        Edit
                      </Button>
                      {project.state === 'in_progress' && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setPauseId(project.id)}
                          className="text-yellow-700 hover:text-yellow-900"
                        >
                          Pause
                        </Button>
                      )}
                      {isAdmin && project.state !== 'archived' && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setArchiveId(project.id)}
                          className="text-red-600 hover:text-red-800"
                        >
                          Archive
                        </Button>
                      )}
                    </td>
                  )}
                </ReparentRow>
              ))}
            </tbody>
          </table>
          {/* Floating row ghost that follows the cursor during a drag. */}
          <DragOverlay dropAnimation={null}>
            {draggedProject ? (
              <div className="flex items-center gap-3 rounded-lg border border-primary-300 bg-white px-4 py-3 shadow-xl cursor-grabbing">
                <span className="font-medium text-primary-600">{draggedProject.name}</span>
                {draggedProject.fullName && draggedProject.fullName !== draggedProject.name && (
                  <span className="text-sm text-gray-500">{draggedProject.fullName}</span>
                )}
              </div>
            ) : null}
          </DragOverlay>
          </DndContext>
        </div>
      )}

      {/* Edit Project Modal */}
      {editingProjectId !== null && (
        <ProjectEditModal projectId={editingProjectId} onClose={() => setEditingProjectId(null)} />
      )}

      {/* Archive Confirmation Modal */}
      <ConfirmModal
        isOpen={archiveId !== null}
        onClose={() => setArchiveId(null)}
        onConfirm={handleArchive}
        title="Archive Project"
        message="Are you sure you want to archive this project? It will be hidden from the project list but can be restored later."
        confirmText="Archive"
        variant="danger"
        isLoading={archiveMutation.isPending}
      />

      {/* Pause Confirmation Modal */}
      <ConfirmModal
        isOpen={pauseId !== null}
        onClose={() => setPauseId(null)}
        onConfirm={handlePause}
        title="Pause Project"
        message="Are you sure you want to pause this project? Users will not be able to log time against it."
        confirmText="Pause"
        variant="primary"
        isLoading={pauseMutation.isPending}
      />

      {/* Merge Projects Modal */}
      {mergeCandidates && (
        <ProjectMergeModal
          isOpen={showMergeModal}
          projectA={mergeCandidates.projectA}
          projectB={mergeCandidates.projectB}
          onClose={() => setShowMergeModal(false)}
          onMerged={handleMerged}
        />
      )}

      {/* Bulk Archive Modal */}
      <ConfirmModal
        isOpen={showBulkArchiveModal}
        onClose={() => setShowBulkArchiveModal(false)}
        onConfirm={handleBulkArchive}
        title="Archive Selected Projects"
        message={`Are you sure you want to archive ${selectedProjectIds.size} project${selectedProjectIds.size > 1 ? 's' : ''}? They will be hidden from the project list but can be restored later.`}
        confirmText={`Archive ${selectedProjectIds.size}`}
        variant="danger"
        isLoading={isBulkArchiving}
      />
    </div>
  );
}
